import catalogJson from "./video-models.json";

export type VideoModelResolution = {
  id: string;
  label: string;
  costSettingId: string;
  creditsPerSecond: number;
  cogsUsdPerSecond: number;
  minCreditsPerSecond: number;
  /** Extra tariff when native audio is on. Omit = same price as silent. */
  audio?: {
    costSettingId: string;
    creditsPerSecond: number;
    cogsUsdPerSecond: number;
    minCreditsPerSecond: number;
  };
};

/** Media kinds the model accepts as multimodal / V2V reference (PoYo docs). */
export type VideoReferenceMedia = "video" | "image" | "audio";

export type VideoModelOption = {
  id: string;
  label: string;
  provider: "kling" | "poyo";
  modalities: ("t2v" | "i2v" | "v2v")[];
  /**
   * Explicit reference inputs (from PoYo/Kling docs). Prefer this over guessing
   * from the label — agents filter the V2V modal with `acceptsVideoReference`.
   * Seedance 2/2.5: video+image+audio · Wan ref: video+image · Wan edit / Kling: video.
   */
  referenceMedia?: VideoReferenceMedia[];
  poyoModel?: string;
  /** Modality-specific PoYo API model IDs (Wan 2.7 / 3.0 families). */
  poyoModelByModality?: Partial<Record<"t2v" | "i2v" | "v2v", string>>;
  klingModel?: string;
  resolutions: VideoModelResolution[];
  durationMin: number;
  durationMax: number;
  /**
   * Max length of the *source* / reference clip the provider accepts (PoYo Wan 2.7 edit
   * and reference-to-video: 2–10s on video_url). When set, the V2V modal hides the model
   * if the board clip is longer.
   */
  sourceDurationMax?: number;
  billingNote?: string;
  premium?: boolean;
  /** Seedance 2.5 V2V bills output duration + reference duration. */
  billedOutputPlusRef?: boolean;
  /** Model can emit a native soundtrack (Seedance generate_audio, Wan 3 audio, Kling sound). */
  supportsGeneratedAudio?: boolean;
  /**
   * Provider always returns a soundtrack; API has no mute flag (e.g. PoYo H3 Max).
   * UI should show “áudio sempre incluso”, not Mudo|Com som.
   */
  alwaysGeneratedAudio?: boolean;
  /** Kling Avatar: billed from driving audio duration; needs image + audio_url. */
  requiresDrivingAudio?: boolean;
};

/**
 * Max images the provider accepts as visual references (not first/last keyframes).
 * Seedance 2.5: 30 · Seedance 2.0 / Mini / H3 Max: 9 · Wan 2.7/3.0: 10 · Kling Omni: 9
 */
export function maxReferenceImages(model: VideoModelOption): number {
  if (requiresDrivingAudio(model)) return 1;
  if (model.id === "poyo-wan-edit") return 1;
  if (model.id === "poyo-seedance25" || model.id === "poyo-seedance25-v2v") return 30;
  if (
    model.id === "poyo-seedance-mini-480" ||
    model.id === "poyo-seedance2" ||
    model.id === "poyo-seedance2-v2v" ||
    model.id === "poyo-h3-max"
  ) {
    return 9;
  }
  if (model.id === "poyo-vidu-q4") return 12;
  if (model.id === "poyo-kling4" || model.id === "poyo-kling4-v2v") return 10;
  if (model.id === "poyo-flux3-max") return 2;
  if (model.id === "poyo-h3-max-turbo") return 2;
  if (model.id.startsWith("poyo-wan")) return 10;
  if (model.provider === "kling") return 9;
  return 2;
}

export function supportsMultiImageReference(model: VideoModelOption): boolean {
  return maxReferenceImages(model) > 2;
}

export function uiMaxReferenceImages(model: VideoModelOption): number {
  return maxReferenceImages(model);
}

/** True when the model can accept at least `count` reference / keyframe images. */
export function modelSupportsReferenceCount(model: VideoModelOption, count: number): boolean {
  if (!Number.isFinite(count) || count <= 0) return true;
  return maxReferenceImages(model) >= count;
}

/** True when the model can generate a clip at least `seconds` long. */
export function modelSupportsDuration(model: VideoModelOption, seconds: number): boolean {
  if (!Number.isFinite(seconds) || seconds <= 0) return true;
  return (model.durationMax || 0) >= seconds;
}

/**
 * Max source/reference clip length the provider accepts.
 * Wan 2.7 edit/reference: 2–10s (`video_url duration must be between 2 and 10 seconds`).
 * Kling 4 Edit: 3–30s. Prefer explicit `sourceDurationMax` on the catalog row.
 */
export function sourceDurationMax(model: VideoModelOption): number | undefined {
  if (typeof model.sourceDurationMax === "number" && model.sourceDurationMax > 0) {
    return model.sourceDurationMax;
  }
  if (model.id === "poyo-wan-edit" || model.id === "poyo-wan27") return 10;
  if (model.id === "kling-omni-v2v") return 10;
  if (model.id === "poyo-kling4-v2v") return 30;
  return undefined;
}

/** True when a reference / base clip of `seconds` fits the provider input limit. */
export function modelSupportsSourceDuration(model: VideoModelOption, seconds: number): boolean {
  if (!Number.isFinite(seconds) || seconds <= 0) return true;
  const max = sourceDurationMax(model);
  if (max == null) return true;
  // PoYo probes the file; 10.01s already fails a max of 10.
  return seconds <= max;
}

/** Beat slots left after reserving global start+end identity anchors. */
export function usableBeatReferenceSlots(model: VideoModelOption): number {
  return Math.max(2, maxReferenceImages(model) - 2);
}

/**
 * True when a single create-video cannot cover sceneCount + duration
 * (needs multi-clip split). Models that support multi-clip stay selectable.
 */
export function planNeedsMultiClip(
  model: VideoModelOption,
  sceneCount: number,
  durationSeconds: number,
): boolean {
  if (sceneCount > maxReferenceImages(model)) return true;
  if (durationSeconds > (model.durationMax || 0)) return true;
  return false;
}

/** Rough part count using refs + duration caps (anchors reserved). */
export function estimateClipPartCount(
  model: VideoModelOption,
  sceneCount: number,
  durationSeconds: number,
): number {
  const maxRefs = maxReferenceImages(model);
  const usable = Math.max(2, maxRefs - 2);
  const byRefs = Math.max(1, Math.ceil(sceneCount / usable));
  const byDur = Math.max(1, Math.ceil(durationSeconds / Math.max(1, model.durationMax || 15)));
  return Math.max(byRefs, byDur);
}

/** Old catalog IDs (pre multi-resolution) → current id + preferred resolution. */
const LEGACY_VIDEO_MODEL_IDS: Record<string, { id: string; resolutionId?: string }> = {
  "poyo-kling-turbo": { id: "kling-omni-i2v" },
  "poyo-wan-edit-720": { id: "poyo-wan-edit", resolutionId: "720p" },
  "poyo-wan-edit-1080": { id: "poyo-wan-edit", resolutionId: "1080p" },
  "poyo-seedance25-v2v-480": { id: "poyo-seedance25-v2v", resolutionId: "480p" },
  "poyo-seedance25-480": { id: "poyo-seedance25", resolutionId: "480p" },
  "poyo-seedance25-720": { id: "poyo-seedance25", resolutionId: "720p" },
};

export function resolvePoyoApiModel(
  model: VideoModelOption,
  modality: "t2v" | "i2v" | "v2v",
  _resolutionId: string,
): string | undefined {
  const byMod = model.poyoModelByModality?.[modality];
  if (byMod) return byMod;
  return model.poyoModel;
}

export const VIDEO_MODEL_CATALOG: VideoModelOption[] = catalogJson as VideoModelOption[];

export function getVideoModelById(id: string): VideoModelOption | undefined {
  return VIDEO_MODEL_CATALOG.find((m) => m.id === id);
}

export function listModelsForModality(modality: "t2v" | "i2v" | "v2v"): VideoModelOption[] {
  return VIDEO_MODEL_CATALOG.filter((m) => m.modalities.includes(modality));
}

/** True when the model can take a reference video (V2V / reference-to-video / edit). */
export function acceptsVideoReference(model: VideoModelOption): boolean {
  if (model.referenceMedia?.includes("video")) return true;
  return model.modalities.includes("v2v");
}

/**
 * Models for the V2V debit modal / agent filter.
 * Pass `provider: 'poyo'` to hide Kling when the org should stay on PoYo.
 */
export function listVideoReferenceModels(opts?: {
  provider?: "poyo" | "kling";
  alsoAccepts?: VideoReferenceMedia[];
}): VideoModelOption[] {
  return listModelsForModality("v2v").filter((m) => {
    if (opts?.provider && m.provider !== opts.provider) return false;
    if (!acceptsVideoReference(m)) return false;
    if (opts?.alsoAccepts?.length) {
      const media = m.referenceMedia || ["video"];
      if (!opts.alsoAccepts.every((k) => media.includes(k))) return false;
    }
    return true;
  });
}

/** Default PoYo model when opening a V2V debit (Seedance 2.5 reference-to-video). */
export function preferredVideoReferenceModel(): VideoModelOption | undefined {
  return getVideoModelById("poyo-seedance25-v2v")
    || listVideoReferenceModels({ provider: "poyo" })[0]
    || listVideoReferenceModels()[0];
}

/**
 * Map an i2v catalog id to its V2V sibling when the modal switches modality
 * (e.g. poyo-seedance25 → poyo-seedance25-v2v).
 */
export function videoReferenceSiblingId(modelId: string): string | undefined {
  if (!modelId) return undefined;
  const direct = getVideoModelById(modelId);
  if (direct && acceptsVideoReference(direct)) return modelId;
  const sibling = `${modelId}-v2v`;
  return getVideoModelById(sibling) ? sibling : undefined;
}

export function resolveResolution(
  model: VideoModelOption,
  resolutionId?: string,
): VideoModelResolution {
  if (!model.resolutions.length) {
    throw new Error(`Modelo ${model.id} sem resoluções configuradas`);
  }
  if (resolutionId) {
    const found = model.resolutions.find((r) => r.id === resolutionId);
    if (found) return found;
  }
  return model.resolutions[0];
}

/** Smallest quality the model offers (480p before 720p before 1080p). */
export function lowestResolution(model: VideoModelOption): VideoModelResolution {
  if (!model.resolutions.length) {
    throw new Error(`Modelo ${model.id} sem resoluções configuradas`);
  }
  const rank = (row: VideoModelResolution) => {
    const pixels = Number.parseInt(row.id, 10);
    return Number.isFinite(pixels) ? pixels : row.creditsPerSecond * 10000;
  };
  return model.resolutions.reduce((best, row) => (rank(row) < rank(best) ? row : best));
}

export function estimateCredits(args: {
  creditsPerSecond: number;
  durationSeconds: number;
  referenceDurationSeconds?: number;
  billedOutputPlusRef?: boolean;
}): number {
  const { creditsPerSecond, durationSeconds, referenceDurationSeconds = 0, billedOutputPlusRef } = args;
  const multiplier = billedOutputPlusRef
    ? durationSeconds + referenceDurationSeconds
    : durationSeconds;
  return Math.ceil(creditsPerSecond * multiplier);
}

export function supportsGeneratedAudio(model: VideoModelOption): boolean {
  return model.supportsGeneratedAudio === true;
}

/** Soundtrack is always present; cannot be toggled off via provider API. */
export function alwaysGeneratedAudio(model: VideoModelOption): boolean {
  return model.alwaysGeneratedAudio === true;
}

export function requiresDrivingAudio(model: VideoModelOption): boolean {
  return model.requiresDrivingAudio === true || String(model.poyoModel || "").startsWith("kling-avatar-2.0");
}

export function rateForGeneratedAudio(
  resolution: VideoModelResolution,
  generateAudio: boolean,
): {
  costSettingId: string;
  creditsPerSecond: number;
  cogsUsdPerSecond: number;
  minCreditsPerSecond: number;
  audioSurcharge: boolean;
} {
  if (generateAudio && resolution.audio) {
    return { ...resolution.audio, audioSurcharge: true };
  }
  return {
    costSettingId: resolution.costSettingId,
    creditsPerSecond: resolution.creditsPerSecond,
    cogsUsdPerSecond: resolution.cogsUsdPerSecond,
    minCreditsPerSecond: resolution.minCreditsPerSecond,
    audioSurcharge: false,
  };
}

/** Prefer native audio when it costs the same as silent (Seedance/Wan). */
export function defaultGenerateAudio(
  model: VideoModelOption,
  resolution = model.resolutions[0],
): boolean {
  if (!supportsGeneratedAudio(model) || !resolution) return false;
  const silent = rateForGeneratedAudio(resolution, false).creditsPerSecond;
  const withAudio = rateForGeneratedAudio(resolution, true).creditsPerSecond;
  return withAudio === silent;
}

export type VideoModelSelection = {
  videoModelId: string;
  resolutionId: string;
  model: VideoModelOption;
  resolution: VideoModelResolution;
};

export function pickVideoModelSelection(args: {
  videoModelId?: string;
  resolutionId?: string;
  modality: "t2v" | "i2v" | "v2v";
  /** v2v_recolor forces Kling Omni */
  forceKlingV2v?: boolean;
}): VideoModelSelection {
  let videoModelId = args.videoModelId;
  let resolutionId = args.resolutionId;
  if (args.forceKlingV2v) {
    videoModelId = "kling-omni-v2v";
  }
  if (!videoModelId) {
    if (args.modality === "v2v") videoModelId = "poyo-seedance25-v2v";
    else if (args.modality === "i2v") videoModelId = "kling-omni-i2v";
    else videoModelId = "poyo-seedance-mini-480";
  }
  const legacy = LEGACY_VIDEO_MODEL_IDS[videoModelId];
  if (legacy) {
    videoModelId = legacy.id;
    if (!resolutionId && legacy.resolutionId) resolutionId = legacy.resolutionId;
  }
  const model = getVideoModelById(videoModelId);
  if (!model) throw new Error(`Modelo de vídeo desconhecido: ${videoModelId}`);
  if (!model.modalities.includes(args.modality)) {
    throw new Error(`Modelo ${model.label} nao suporta modalidade ${args.modality}`);
  }
  const resolution = resolveResolution(model, resolutionId);
  return {
    videoModelId: model.id,
    resolutionId: resolution.id,
    model,
    resolution,
  };
}

export type VideoRequestComplexity = "simple" | "medium" | "complex";

const COMPLEX_VIDEO_RE =
  /cinemat|filme|luxo|luxury|arquitet|detalhad|4k|neon|chuva|tempestade|noite|noturno|a[cç][aã]o|epic|dram[aá]tic|campanha|comercial|hero|orbit|crane|dolly|steadicam|c[aâ]mera lenta|slow[\s-]?mo|v[aá]rias pessoas|multipl|fuma[cç]a|neblina|reflexo|ilumina[cç]|volumetr|produto premium|15\s*s|20\s*s|30\s*s/i;
const SIMPLE_VIDEO_RE =
  /simples|s[oó] (um )?zoom|foto parada|leve movimento|sutil|rascunho|teste|preview|barato|walk[\s-]?through simples|andar em linha|um gesto/i;

/** Seedance 2.5 / Wan Prime first when the ask is cinematic; Mini / Wan 2.7 when it is simple. */
const SCENE_MODEL_TIER: Record<VideoRequestComplexity, string[]> = {
  complex: [
    "poyo-seedance25",
    "poyo-wan3-prime",
    "poyo-seedance2",
    "poyo-wan3",
    "kling-omni-i2v",
    "poyo-wan27",
    "poyo-seedance-mini-480",
  ],
  medium: [
    "poyo-seedance2",
    "poyo-wan3",
    "poyo-seedance25",
    "poyo-wan3-prime",
    "kling-omni-i2v",
    "poyo-wan27",
    "poyo-seedance-mini-480",
  ],
  simple: [
    "poyo-seedance-mini-480",
    "poyo-wan27",
    "poyo-wan3",
    "kling-omni-i2v",
    "poyo-seedance2",
    "poyo-wan3-prime",
    "poyo-seedance25",
  ],
};

const AVATAR_MODEL_TIER: Record<VideoRequestComplexity, string[]> = {
  complex: ["poyo-omnihuman2", "poyo-kling-avatar-pro", "poyo-kling-avatar-standard"],
  medium: ["poyo-kling-avatar-standard", "poyo-omnihuman2", "poyo-kling-avatar-pro"],
  simple: ["poyo-kling-avatar-standard", "poyo-omnihuman2", "poyo-kling-avatar-pro"],
};

const V2V_MODEL_TIER: Record<VideoRequestComplexity, string[]> = {
  complex: [
    "poyo-seedance25-v2v",
    "poyo-wan3-prime",
    "poyo-seedance2-v2v",
    "poyo-wan3",
    "kling-omni-v2v",
    "poyo-wan27",
    "poyo-wan-edit",
  ],
  medium: [
    "poyo-seedance2-v2v",
    "poyo-wan3",
    "poyo-seedance25-v2v",
    "kling-omni-v2v",
    "poyo-wan3-prime",
    "poyo-wan27",
    "poyo-wan-edit",
  ],
  simple: [
    "kling-omni-v2v",
    "poyo-wan-edit",
    "poyo-wan27",
    "poyo-wan3",
    "poyo-seedance2-v2v",
    "poyo-seedance25-v2v",
    "poyo-wan3-prime",
  ],
};

export function inferVideoRequestComplexity(text: string): VideoRequestComplexity {
  const t = text || "";
  if (COMPLEX_VIDEO_RE.test(t)) return "complex";
  if (SIMPLE_VIDEO_RE.test(t)) return "simple";
  const durationRe = /(\d+)\s*s(?:eg(?:undos?)?)?/gi;
  let match: RegExpExecArray | null;
  while ((match = durationRe.exec(t))) {
    const n = Number(match[1]);
    if (Number.isFinite(n) && n >= 12) return "complex";
  }
  return "medium";
}

/** Quoted line / generic presenter. Not Kling Avatar by itself. */
export function looksLikeSpokenDialogue(text: string): boolean {
  return /pessoa falando|falando para (a )?c[aâ]mera|fala para (a )?c[aâ]mera|(homem|mulher|pessoa)[^\n.]{0,80}falando|\bfalando\b|locutor|apresentador|depoimento|talking\s*head|l[aá]bios?|dublagem|diz(endo)? a frase|diga a frase/i.test(text || "");
}

/** Lip-sync a real face. Generic “homem de terno falando” is Seedance, not this. */
export function looksLikeAvatarVideoRequest(text: string): boolean {
  const t = text || "";
  if (/n[aã]o.{0,48}\bavatar\b|sem avatar/i.test(t)) return false;
  if (/\bkling[\s-]?avatar\b|\bavatars?\b/i.test(t)) return true;
  if (/lip[\s-]?sync|sincroniz\w*\s+(o\s+)?l[aá]bio/i.test(t)) return true;
  if (/foto do rosto|retrato (da pessoa|do rosto)|purpose.?avatar/i.test(t)) return true;
  if (/(esta|essa|minha) (foto|imagem).{0,48}(fal|avatar|l[aá]bio|dubl)/i.test(t)) return true;
  if (/use (esta|essa) (foto|imagem)/i.test(t)) return true;
  if (/(esta|essa) pessoa/i.test(t)) return true;
  return false;
}

function maxCreditsPerSecond(model: VideoModelOption): number {
  return Math.max(0, ...model.resolutions.map((r) => r.creditsPerSecond || 0));
}

export function rankVideoModelsForRequest(args: {
  modality: "t2v" | "i2v" | "v2v";
  text?: string;
  preferredIds?: string[];
  complexity?: VideoRequestComplexity;
}): VideoModelOption[] {
  const models = listModelsForModality(args.modality);
  const text = args.text || "";
  const complexity = args.complexity || inferVideoRequestComplexity(text);
  const avatar = looksLikeAvatarVideoRequest(text);
  const preferred = (args.preferredIds || []).filter((id) => models.some((m) => m.id === id));
  const tier = args.modality === "v2v"
    ? V2V_MODEL_TIER[complexity]
    : avatar && args.modality === "i2v"
      ? [...AVATAR_MODEL_TIER[complexity], ...SCENE_MODEL_TIER[complexity]]
      : SCENE_MODEL_TIER[complexity];
  const seen = new Set<string>();
  const order: string[] = [];
  for (const id of [...preferred, ...tier]) {
    if (seen.has(id) || !models.some((m) => m.id === id)) continue;
    seen.add(id);
    order.push(id);
  }
  const index = new Map(order.map((id, i) => [id, i]));
  return [...models].sort((a, b) => {
    const ia = index.has(a.id) ? (index.get(a.id) as number) : 800;
    const ib = index.has(b.id) ? (index.get(b.id) as number) : 800;
    if (ia !== ib) return ia - ib;
    const avatarA = requiresDrivingAudio(a) ? 1 : 0;
    const avatarB = requiresDrivingAudio(b) ? 1 : 0;
    if (!avatar && avatarA !== avatarB) return avatarA - avatarB;
    const ca = maxCreditsPerSecond(a);
    const cb = maxCreditsPerSecond(b);
    return complexity === "simple" ? ca - cb : cb - ca;
  });
}
